import { PublicClientApplication } from "@azure/msal-browser";

import { apiScopes, msalConfig } from "../lib/msalConfig";

interface LoginResult {
  type: "login";
  status: "success" | "failure";
  userName?: string;
  error?: string;
}

class MSAuthLibraryService {
  private initPromise: Promise<void> | null = null;

  instance: PublicClientApplication | null = null;

  constructor() {
    this.initPromise = this.initialize();
  }

  private async initialize() {
    if (!this.instance) {
      this.instance = new PublicClientApplication(msalConfig);
      await this.instance.initialize();
    }
  }

  /** Ensures MSAL is fully initialized before using the instance. */
  private async ensureInitialized() {
    await this.initPromise;
  }

  /**
   * Opens an Office dialog that performs MSAL loginRedirect.
   * The dialog sends the token back via Office.context.ui.messageParent.
   */
  login(): Promise<LoginResult> {
    const dialogLoginUrl =
      window.location.protocol +
      "//" +
      window.location.hostname +
      (window.location.port ? ":" + window.location.port : "") +
      "/login-redirect.html";

    return new Promise((resolve, reject) => {
      let isSettled = false;
      const safeResolve = (value: LoginResult) => {
        if (isSettled) return;
        isSettled = true;
        resolve(value);
      };
      const safeReject = (reason: Error) => {
        if (isSettled) return;
        isSettled = true;
        reject(reason);
      };

      Office.context.ui.displayDialogAsync(
        dialogLoginUrl,
        { height: 60, width: 30 },
        (asyncResult) => {
          if (asyncResult.status === Office.AsyncResultStatus.Failed) {
            safeReject(new Error(`${asyncResult.error.code} ${asyncResult.error.message}`));
            return;
          }

          const dialog = asyncResult.value;

          dialog.addEventHandler(
            Office.EventType.DialogMessageReceived,
            (arg: { message: string; origin: string }) => {
              if (arg.origin !== window.location.origin) {
                dialog.close();
                safeReject(new Error("Incorrect origin in login dialog message."));
                return;
              }
              dialog.close();
              let result: LoginResult;
              try {
                result = JSON.parse(arg.message);
              } catch {
                safeReject(new Error("Failed to parse login dialog response."));
                return;
              }
              safeResolve(result);
            }
          );

          dialog.addEventHandler(Office.EventType.DialogEventReceived, (arg: { error: number }) => {
            // User closed the dialog manually
            if (arg.error === 12006) {
              safeResolve({ type: "login", status: "failure", error: "Dialog closed by user." });
            } else {
              safeReject(new Error(`Dialog error: ${arg.error}`));
            }
          });
        }
      );
    });
  }

  async getToken(): Promise<string | null> {
    await this.ensureInitialized();

    const accounts = this.instance?.getAllAccounts() ?? [];
    if (accounts.length === 0) {
      return null;
    }

    try {
      const tokenResponse = await this.instance!.acquireTokenSilent({
        scopes: apiScopes,
        account: accounts[0],
      });
      return tokenResponse.accessToken;
    } catch (error) {
      console.warn("Silent token acquisition failed:", error);
      return null;
    }
  }
}

export const msalService = new MSAuthLibraryService();
